import { API_PREFIX, HttpError, paginatedFromDto, type HttpClient } from "@nasam/kernel";
import type { OperationalUsersRepository } from "../domain";
import {
  archivedOperationalUserListResponseDtoSchema,
  archivedOperationalUserMatchResponseDtoSchema,
  operationalUserCreateDtoSchema,
  operationalUserListResponseDtoSchema,
  operationalUserMessageResponseDtoSchema,
  operationalUserResponseDtoSchema,
  operationalUserUpdateDtoSchema,
} from "./operationalUserDtos";
import {
  archivedOperationalUserFromDto,
  archivedOperationalUserMatchFromDto,
  operationalUserCreateToDto,
  operationalUserDetailsFromDto,
  operationalUserFromDto,
  operationalUserUpdateToDto,
} from "./operationalUserMappers";

const USERS_PATH = `${API_PREFIX}/users`;

/**
 * Every `/users` endpoint the Operational Users screens use.
 */
export function createOperationalUsersHttpRepository(
  http: HttpClient,
): OperationalUsersRepository {
  return {
    // The query's names (search, status, branchId, departmentId, sort,
    // sortBy) are the backend's own, so it goes out as-is.
    async list(query) {
      const response = await http.get(USERS_PATH, { params: query });
      return paginatedFromDto(
        operationalUserListResponseDtoSchema.parse(response),
        operationalUserFromDto,
      );
    },

    async listArchived(query) {
      const response = await http.get(`${USERS_PATH}/archived`, {
        params: query,
      });
      return paginatedFromDto(
        archivedOperationalUserListResponseDtoSchema.parse(response),
        archivedOperationalUserFromDto,
      );
    },

    async getById(id) {
      const response = await http.get(`${USERS_PATH}/${id}`);
      return operationalUserDetailsFromDto(
        operationalUserResponseDtoSchema.parse(response).data,
      );
    },

    // templateId is deliberately left out of the request -- the final
    // `permissions` set always replaces the template's, so there is nothing
    // for the backend to copy from it.
    async create(input) {
      const dto = operationalUserCreateDtoSchema.parse(
        operationalUserCreateToDto(input),
      );
      try {
        const response = await http.post(USERS_PATH, dto);
        const created = operationalUserResponseDtoSchema.parse(response);
        return {
          status: "created",
          user: operationalUserFromDto(created.data),
        };
      } catch (error) {
        if (error instanceof HttpError && error.status === 409) {
          // ARCHIVED_USER_FOUND: an archived user holds the username, email,
          // phone number or either name -- offer to restore it.
          const archived = archivedOperationalUserMatchResponseDtoSchema.safeParse(
            error.data,
          );
          if (archived.success) {
            return {
              status: "archived-match",
              match: archivedOperationalUserMatchFromDto(archived.data.data),
              ...(archived.data.message === undefined
                ? {}
                : { message: archived.data.message }),
            };
          }
          // Otherwise a living user holds the username or email; the
          // localized message says which.
          return { status: "duplicate", message: error.message };
        }
        throw error;
      }
    },

    // `permissions` always replaces the whole set; username and email can't
    // be sent here at all (fixed after creation). Any refusal (a branch or
    // department gone inactive, a permission not grantable, or trying to
    // force-activate a never-activated user) rethrows -- the mutation hook
    // turns it into a generic failure carrying the backend's own message.
    async update(id, input) {
      const dto = operationalUserUpdateDtoSchema.parse(
        operationalUserUpdateToDto(input),
      );
      const response = await http.patch(`${USERS_PATH}/${id}`, dto);
      const updated = operationalUserResponseDtoSchema.parse(response);
      return {
        status: "updated",
        user: operationalUserDetailsFromDto(updated.data),
      };
    },

    // The backend archives (soft delete); `restore` brings the user back.
    // A 404 (gone, or another account's) surfaces as an HttpError with the
    // backend's localized message, which the global toast shows.
    async archive(id) {
      const response = await http.delete(`${USERS_PATH}/${id}`);
      operationalUserMessageResponseDtoSchema.parse(response);
    },

    // Refused with 404 (not archived / another account's) or 500 (nothing
    // changed); the HttpError keeps the backend's localized reason.
    async restore(id) {
      const response = await http.patch(`${USERS_PATH}/${id}/restore`);
      return operationalUserDetailsFromDto(
        operationalUserResponseDtoSchema.parse(response).data,
      );
    },

    // Only for a user still pending activation, mirroring the drivers
    // endpoint shape (no operational-users contract is published yet).
    async resendActivation(id) {
      const response = await http.post(
        `${USERS_PATH}/${id}/resend-activation-link`,
      );
      operationalUserMessageResponseDtoSchema.parse(response);
    },
  };
}
